import React from "react";
import { Palette } from "../holidayConfig";
import { mix } from "./kit";
import { range } from "./sceneUtils";
import { Blush, Contact, Eye, Shaded, ShadeColors, Sphere, useToonId } from "./toon";

/*
 * Påskens figurer: påskharar, kycklingar, målade ägg, påskliljor,
 * fjädrar och påskkärringen på sin kvast med kaffepannan.
 */

export const LEAF: ShadeColors = { base: "#8fae5a", shade: "#5f7d36", light: "#c3d98f" };
const SKIN: ShadeColors = { base: "#ffd8c2", shade: "#eab39a", light: "#fff1e8", rim: "#f6c6ae" };
const EYE = "#3a2410";

/* ------------------------------------------------------------------ */
/* Målat ägg                                                            */
/* ------------------------------------------------------------------ */
export type EggPattern = "zigzag" | "dots" | "bands" | "flowers";

export const EGG_PATH = "M0 -60 C -24 -60, -38 -26, -38 -4 C -38 16, -22 30, 0 30 C 22 30, 38 16, 38 -4 C 38 -26, 24 -60, 0 -60 Z";

/** Glansigt påskägg med mönster. (0, 0) = äggets mitt, höjd 90. */
export const Egg: React.FC<{ x: number; y: number; s?: number; rot?: number; base: ShadeColors; ink: string; accent: string; pattern: EggPattern; shadow?: boolean }> = ({
  x,
  y,
  s = 1,
  rot = 0,
  base,
  ink,
  accent,
  pattern,
  shadow = true,
}) => {
  const id = useToonId("egg");
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot}) scale(${s})`}>
      {shadow && <Contact cx={6} cy={30} rx={40} ry={7} color="#7a4a10" opacity={0.3} />}
      <defs>
        <clipPath id={id}>
          <path d={EGG_PATH} />
        </clipPath>
      </defs>
      <Shaded d={EGG_PATH} c={base} depth={11} />
      <g clipPath={`url(#${id})`}>
        {pattern === "zigzag" && (
          <>
            <polyline points="-44,-10 -33,-22 -22,-10 -11,-22 0,-10 11,-22 22,-10 33,-22 44,-10" fill="none" stroke={ink} strokeWidth="6" strokeLinejoin="round" />
            <rect x="-44" y="2" width="88" height="4" fill={accent} />
            <rect x="-44" y="10" width="88" height="3" fill={ink} opacity="0.8" />
            {[-20, 0, 20].map(cx => (
              <circle key={cx} cx={cx} cy={-38} r="3.2" fill={accent} />
            ))}
          </>
        )}
        {pattern === "dots" &&
          range(18).map(k => {
            const cx = -30 + (k % 6) * 12 + (Math.floor(k / 6) % 2) * 6;
            const cy = -44 + Math.floor(k / 6) * 22;
            return <circle key={k} cx={cx} cy={cy} r={k % 3 === 0 ? 4.2 : 3} fill={k % 2 ? ink : accent} />;
          })}
        {pattern === "bands" && (
          <>
            <rect x="-44" y="-30" width="88" height="7" fill={ink} />
            <rect x="-44" y="-19" width="88" height="3" fill={accent} />
            <rect x="-44" y="6" width="88" height="3" fill={accent} />
            <rect x="-44" y="13" width="88" height="7" fill={ink} />
            {range(7).map(k => (
              <path key={k} d={`M${-33 + k * 11} -8 l 4 -6 l 4 6 l -4 6 Z`} fill={ink} opacity="0.85" />
            ))}
          </>
        )}
        {pattern === "flowers" &&
          [
            [-14, -30],
            [14, -12],
            [-18, 8],
            [16, 16],
            [0, -48],
          ].map(([fx, fy], k) => (
            <g key={k} transform={`translate(${fx} ${fy})`}>
              {range(5).map(i => (
                <ellipse key={i} cx={0} cy={-5} rx={3} ry={5} fill={accent} transform={`rotate(${i * 72})`} />
              ))}
              <circle r="2.6" fill={ink} />
            </g>
          ))}
      </g>
      <ellipse cx={-14} cy={-36} rx={7} ry={13} transform="rotate(-22 -14 -36)" fill="#ffffff" opacity="0.6" filter="url(#toon-soft)" />
      <ellipse cx={-17} cy={-40} rx={2.5} ry={5} transform="rotate(-22 -17 -40)" fill="#ffffff" opacity="0.9" />
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Påskhare                                                             */
/* ------------------------------------------------------------------ */
export const Bunny: React.FC<{ x: number; y: number; s?: number; flip?: boolean; p: Palette; egg?: boolean }> = ({ x, y, s = 1, flip, p, egg = true }) => {
  const fur: ShadeColors = { base: "#fffaf2", shade: mix("#f1dcc0", p[300], 0.25), light: "#ffffff", rim: "#f8e9d4" };
  const inner = "#ffc9c2";
  return (
    <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
      <Contact cx={0} cy={0} rx={58} ry={8} color="#8a5a14" opacity={0.28} />
      {/* Svans */}
      <Sphere cx={-40} cy={-24} r={12} c={fur} />
      {/* Kropp */}
      <Shaded d="M-44 -2 C -50 -40, -30 -70, 0 -70 C 28 -70, 44 -44, 38 -2 Z" c={fur} depth={12} />
      {/* Bakfot */}
      <Shaded d="M-34 0 C -40 -4, -38 -16, -22 -16 L 6 -16 C 16 -16, 16 0, 6 0 Z" c={fur} depth={4} />
      {/* Öron */}
      <g>
        <Shaded d="M-8 -118 C -22 -150, -22 -186, -8 -190 C 6 -192, 8 -154, 4 -118 Z" c={fur} depth={6} />
        <path d="M-6 -126 C -15 -152, -15 -178, -7 -181 C 0 -182, 1 -152, 0 -126 Z" fill={inner} />
        <Shaded d="M14 -116 C 18 -150, 36 -176, 48 -172 C 58 -166, 40 -140, 26 -114 Z" c={fur} depth={6} />
        <path d="M17 -122 C 22 -148, 36 -166, 44 -164 C 50 -160, 36 -140, 25 -121 Z" fill={inner} />
      </g>
      {/* Huvud */}
      <Shaded
        d="M-30 -100 C -34 -128, -14 -142, 6 -142 C 28 -142, 44 -126, 40 -100 C 38 -78, 22 -68, 4 -68 C -16 -68, -28 -80, -30 -100 Z"
        c={fur}
        depth={10}
        highlight={{ cx: -10, cy: -128, rx: 12, ry: 6, rot: -20, opacity: 0.8 }}
      />
      <Eye x={-8} y={-108} rx={6} ry={7.6} iris={EYE} look={[0.25, 0.1]} />
      <Eye x={18} y={-108} rx={6.4} ry={8} iris={EYE} look={[0.25, 0.1]} />
      <Blush x={-16} y={-92} r={6.5} color="#ffab9e" opacity={0.6} />
      <Blush x={28} y={-92} r={6.5} color="#ffab9e" opacity={0.6} />
      <path d="M3 -94 C 5 -98, 11 -98, 13 -94 C 11 -91, 5 -91, 3 -94 Z" fill="#ff8f86" />
      <path d="M8 -91 V -87 M 8 -87 C 5 -83, 1 -84, 0 -86 M 8 -87 C 11 -83, 15 -84, 16 -86" stroke="#a0605a" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <path d="M-4 -90 L -20 -94 M -4 -88 L -20 -86 M 20 -90 L 36 -94 M 20 -88 L 36 -86" stroke="#d8c0a8" strokeWidth="0.9" />
      {/* Framtassar med ägg */}
      {egg && (
        <Egg
          x={10}
          y={-44}
          s={0.36}
          rot={-8}
          base={{ base: p[300], shade: p[500], light: p[100], rim: p[400] }}
          ink="#ffffff"
          accent={p[600]}
          pattern="zigzag"
          shadow={false}
        />
      )}
      <Shaded d="M-12 -46 C -18 -48, -18 -38, -10 -36 C -4 -35, -2 -42, -6 -46 Z" c={fur} depth={3} />
      <Shaded d="M22 -46 C 28 -48, 30 -38, 22 -36 C 16 -35, 14 -42, 18 -46 Z" c={fur} depth={3} />
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Kyckling                                                             */
/* ------------------------------------------------------------------ */
export const Chick: React.FC<{ x: number; y: number; s?: number; flip?: boolean; p: Palette; shell?: boolean }> = ({ x, y, s = 1, flip, p, shell }) => {
  const down: ShadeColors = { base: p[300], shade: p[500], light: p[100], rim: p[400] };
  return (
    <g transform={`translate(${x} ${y}) scale(${flip ? -s : s} ${s})`}>
      <Contact cx={0} cy={0} rx={30} ry={5} color="#8a5a14" opacity={0.3} />
      {!shell && (
        <path d="M-8 -2 L -10 2 M -8 -2 L -6 2 M 6 -2 L 4 2 M 6 -2 L 8 2 M -8 -8 V -2 M 6 -8 V -2" stroke={p[600]} strokeWidth="2" strokeLinecap="round" />
      )}
      <Sphere cx={0} cy={-24} r={20} c={down} />
      {/* Tofs */}
      <path d="M-2 -44 C -6 -54, 2 -56, 0 -48 C 4 -56, 10 -52, 3 -44" fill={down.base} />
      {/* Vinge */}
      <Shaded d="M-18 -26 C -30 -30, -30 -14, -16 -12 Z" c={down} depth={3} />
      <Eye x={-2} y={-30} rx={3.6} ry={4.4} iris={EYE} look={[0.3, 0.1]} />
      <Eye x={10} y={-30} rx={3.6} ry={4.4} iris={EYE} look={[0.3, 0.1]} />
      <path d="M2 -24 L 12 -21 L 3 -18 Z" fill={p[600]} />
      <path d="M2 -21 L 12 -21 L 3 -18 Z" fill={p[700]} opacity="0.5" />
      <Blush x={-8} y={-21} r={3.5} color="#ff9f8a" opacity={0.6} />
      {shell && (
        <g>
          <Shaded d="M-24 -18 L -16 -24 L -8 -16 L 0 -24 L 8 -16 L 16 -24 L 24 -18 C 26 -4, 16 4, 0 4 C -16 4, -26 -4, -24 -18 Z" c={{ base: "#fffdf8", shade: "#ead8c0", light: "#ffffff" }} depth={6} />
          <path d="M-20 -8 C -12 -4, 12 -4, 20 -8" stroke={p[400]} strokeWidth="2.5" fill="none" />
        </g>
      )}
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Påsklilja                                                            */
/* ------------------------------------------------------------------ */
export const Daffodil: React.FC<{ x: number; y: number; s?: number; lean?: number; p: Palette; delay?: number }> = ({ x, y, s = 1, lean = 0, p, delay = 0 }) => {
  const petal: ShadeColors = { base: p[200], shade: p[300], light: "#fffbe8" };
  const cup: ShadeColors = { base: p[400], shade: p[600], light: p[200] };
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <g className="hol-sway-base" style={{ animationDelay: `${delay}s`, animationDuration: `${5 + (delay % 3)}s` }}>
        <path d={`M0 0 C 2 -40, ${lean * 0.5} -80, ${lean} -110`} stroke={LEAF.shade} strokeWidth="4" fill="none" strokeLinecap="round" />
        <Shaded d="M-2 0 C -14 -30, -18 -60, -10 -86 C -4 -60, -2 -30, 2 0 Z" c={LEAF} depth={3} />
        <Shaded d="M2 0 C 16 -24, 22 -50, 18 -72 C 10 -50, 6 -26, 4 0 Z" c={LEAF} depth={3} />
        <g transform={`translate(${lean} -112) rotate(${lean * 0.8 + 18})`}>
          {range(6).map(i => (
            <g key={i} transform={`rotate(${i * 60})`}>
              <Shaded d="M0 0 C -8 -6, -8 -20, 0 -26 C 8 -20, 8 -6, 0 0 Z" c={petal} depth={3} />
            </g>
          ))}
          <Shaded d="M-9 -2 C -10 8, -8 16, -11 20 C -4 23, 4 23, 11 20 C 8 16, 10 8, 9 -2 C 6 -6, -6 -6, -9 -2 Z" c={cup} depth={4} />
          <path d="M-11 20 C -8 24, -4 22, -2 25 C 1 22, 4 24, 6 22 C 8 24, 11 22, 11 20" stroke={p[600]} strokeWidth="1.6" fill="none" />
        </g>
      </g>
    </g>
  );
};

/* ------------------------------------------------------------------ */
/* Fjäder                                                               */
/* ------------------------------------------------------------------ */
export const Feather: React.FC<{ color: string; shade: string }> = ({ color, shade }) => (
  <g>
    <path d="M0 0 C -9 -10, -12 -32, -2 -52 C 2 -58, 6 -56, 8 -50 C 14 -30, 10 -12, 0 0 Z" fill={color} />
    <path d="M0 0 C 2 -18, 3 -36, 3 -54" stroke={shade} strokeWidth="1.3" fill="none" />
    {range(7).map(i => (
      <path key={i} d={`M${1 + i * 0.3} ${-8 - i * 6} l ${i % 2 ? 7 : -8} ${-4 - (i % 3)}`} stroke={shade} strokeWidth="0.8" opacity="0.6" />
    ))}
    <path d="M-4 -30 C -7 -34, -8 -40, -6 -44" stroke="#ffffff" strokeWidth="1.6" opacity="0.6" fill="none" strokeLinecap="round" />
  </g>
);

/* ------------------------------------------------------------------ */
/* Påskkärringen                                                        */
/* ------------------------------------------------------------------ */
export const Witch: React.FC<{ p: Palette }> = ({ p }) => {
  const dress: ShadeColors = { base: p[500], shade: p[700], light: p[300], rim: p[600] };
  const scarf: ShadeColors = { base: "#ffffff", shade: p[200], light: "#ffffff" };
  const apron: ShadeColors = { base: "#fffdf6", shade: "#efe2c8", light: "#ffffff" };
  const copper: ShadeColors = { base: p[600], shade: p[800], light: p[300], rim: p[700] };
  const wood = mix(p[800], "#7a5230", 0.5);
  const cat: ShadeColors = { base: "#3a2e2a", shade: "#1a1311", light: "#6a5a54" };
  return (
    <svg viewBox="-130 -110 260 170">
      {/* Kvasten */}
      <path d="M-86 12 L 110 -22" stroke={wood} strokeWidth="5" strokeLinecap="round" />
      <path d="M-84 11 L 108 -22" stroke="#ffffff" strokeOpacity="0.25" strokeWidth="1.5" />
      <Shaded d="M-82 4 L -128 -6 L -126 8 L -130 22 L -118 30 L -84 18 Z" c={{ base: p[300], shade: p[500], light: p[100] }} depth={4} />
      {range(6).map(i => (
        <path key={i} d={`M-86 ${8 + i * 1.5} L ${-126 + (i % 2) * 4} ${-2 + i * 5}`} stroke={p[600]} strokeWidth="0.9" opacity="0.6" />
      ))}
      <path d="M-86 4 L -84 20" stroke={p[700]} strokeWidth="4" />
      {/* Kaffepannan hänger i kvasten */}
      <g transform="translate(76 -16)">
        <path d="M0 0 V 12" stroke="#5a4030" strokeWidth="1.2" />
        <Shaded d="M-14 34 C -18 22, -12 12, 0 12 C 12 12, 18 22, 14 34 Z" c={copper} depth={5} highlight={{ cx: -6, cy: 20, rx: 3, ry: 6, rot: 10, opacity: 0.8 }} />
        <path d="M-15 34 H 15" stroke={p[800]} strokeWidth="3" strokeLinecap="round" />
        <path d="M12 20 C 20 18, 24 14, 28 10" stroke={p[700]} strokeWidth="3.5" fill="none" strokeLinecap="round" />
        <path d="M-12 18 C -22 18, -22 30, -13 30" stroke={p[800]} strokeWidth="2.5" fill="none" />
        <rect x="-4" y="9" width="8" height="4" rx="2" fill={p[800]} />
        <path className="hol-smoke" d="M28 6 c 4 -6, -2 -10, 3 -16" stroke="#ffffff" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.7" />
      </g>
      {/* Katten på kvastens bakdel */}
      <g transform="translate(-58 6)">
        <path d="M-10 0 C -26 -2, -30 -18, -22 -26" stroke={cat.base} strokeWidth="4" fill="none" strokeLinecap="round" />
        <Shaded d="M-12 2 C -14 -14, -6 -22, 2 -22 C 10 -22, 14 -12, 12 2 Z" c={cat} depth={4} />
        <Shaded d="M-8 -26 L -10 -38 L -3 -32 C 0 -33, 3 -33, 5 -32 L 12 -38 L 10 -26 C 10 -20, 6 -17, 1 -17 C -4 -17, -8 -20, -8 -26 Z" c={cat} depth={3} />
        <ellipse cx="-3" cy="-27" rx="2.4" ry="2.8" fill={p[300]} />
        <ellipse cx="5" cy="-27" rx="2.4" ry="2.8" fill={p[300]} />
        <ellipse cx="-3" cy="-27" rx="0.8" ry="2.2" fill="#111" />
        <ellipse cx="5" cy="-27" rx="0.8" ry="2.2" fill="#111" />
      </g>
      {/* Ben med randiga strumpor */}
      <g>
        <path d="M18 -2 C 26 10, 30 22, 30 34" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" fill="none" />
        <path d="M18 -2 C 26 10, 30 22, 30 34" stroke={p[600]} strokeWidth="8" strokeDasharray="4 4" fill="none" />
        <path d="M24 34 C 26 40, 40 42, 42 36 C 42 32, 34 30, 28 30 Z" fill="#2a1a10" />
        <path d="M4 0 C 10 12, 12 24, 10 36" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" fill="none" />
        <path d="M4 0 C 10 12, 12 24, 10 36" stroke={p[600]} strokeWidth="8" strokeDasharray="4 4" fill="none" />
        <path d="M4 36 C 6 42, 20 44, 22 38 C 22 34, 14 32, 8 32 Z" fill="#2a1a10" />
      </g>
      {/* Kjol som fladdrar över kvasten */}
      <Shaded d="M-34 -6 C -40 -34, -10 -46, 12 -44 C 30 -42, 38 -26, 34 -8 C 30 8, 20 12, 8 4 C -2 14, -14 12, -18 4 C -26 10, -36 6, -34 -6 Z" c={dress} depth={9} highlight={{ cx: -10, cy: -34, rx: 10, ry: 4, rot: -10, opacity: 0.5 }} />
      <Shaded d="M-4 -40 C 6 -40, 22 -36, 26 -22 C 28 -10, 22 -2, 14 -4 C 10 -16, 4 -28, -4 -40 Z" c={apron} depth={5} />
      {/* Överkropp och arm */}
      <Shaded d="M-8 -44 C -12 -64, -2 -76, 10 -76 C 22 -76, 28 -62, 24 -44 Z" c={dress} depth={6} />
      <Shaded d="M16 -60 C 28 -56, 38 -44, 44 -34 L 38 -30 C 32 -38, 24 -46, 14 -50 Z" c={dress} depth={3} />
      <Sphere cx={44} cy={-31} r={5.5} c={SKIN} />
      {/* Huvud med sjalett */}
      <Shaded d="M-8 -104 C -24 -104, -30 -84, -22 -70 C -16 -60, -4 -58, 2 -60 L 30 -76 C 34 -96, 22 -108, -8 -104 Z" c={scarf} depth={6} />
      <Sphere cx={10} cy={-86} r={17} c={SKIN} />
      <Shaded d="M-8 -96 C -6 -110, 26 -112, 30 -94 C 22 -100, 4 -102, -8 -96 Z" c={scarf} depth={3} />
      {range(6).map(i => (
        <circle key={i} cx={-16 + (i % 3) * 9} cy={-96 + Math.floor(i / 3) * 16} r={2} fill={p[400]} />
      ))}
      <path d="M-2 -70 L -14 -58 L -6 -56 Z" fill={scarf.shade} />
      <Eye x={9} y={-89} rx={3.4} ry={4.2} iris={EYE} look={[0.35, 0.05]} />
      <Eye x={21} y={-89} rx={3.2} ry={4} iris={EYE} look={[0.35, 0.05]} />
      <Blush x={6} y={-78} r={4.5} color="#ff9486" opacity={0.7} />
      <Blush x={24} y={-79} r={3.5} color="#ff9486" opacity={0.7} />
      {[
        [4, -81],
        [7, -83],
        [22, -82],
        [25, -80],
      ].map(([fx, fy], i) => (
        <circle key={i} cx={fx} cy={fy} r={0.8} fill="#c07050" />
      ))}
      <path d="M12 -77 C 15 -73, 20 -73, 23 -77" stroke="#9a4a3a" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <Shaded d="M24 -82 C 28 -82, 29 -78, 26 -76 Z" c={SKIN} depth={1} />
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* Hängande ägg med fjädrar (påskris)                                   */
/* ------------------------------------------------------------------ */
export const HangingEgg: React.FC<{ p: Palette; thread: number; variant: number }> = ({ p, thread, variant }) => {
  const bases: ShadeColors[] = [
    { base: p[300], shade: p[500], light: p[100], rim: p[400] },
    { base: "#fffdf7", shade: "#f0dfc4", light: "#ffffff" },
    { base: p[400], shade: p[600], light: p[200], rim: p[500] },
    { base: p[200], shade: p[400], light: "#fffbe8", rim: p[300] },
  ];
  const patterns: EggPattern[] = ["zigzag", "dots", "bands", "flowers"];
  const inks = ["#ffffff", p[500], "#ffffff", p[600]];
  const accents = [p[600], p[300], p[700], "#ffffff"];
  const v = variant % 4;
  return (
    <svg viewBox={`-50 0 100 ${thread + 100}`}>
      <line x1="0" y1="0" x2="0" y2={thread + 4} stroke={p[600]} strokeOpacity="0.55" strokeWidth="1" />
      {/* Rosett */}
      <g transform={`translate(0 ${thread + 4})`}>
        <path d="M0 0 C -6 -8, -16 -8, -14 0 C -12 6, -4 4, 0 0 Z M 0 0 C 6 -8, 16 -8, 14 0 C 12 6, 4 4, 0 0 Z" fill={v === 1 ? p[400] : "#ffffff"} />
        <circle r="2.6" fill={v === 1 ? p[500] : "#f7ecd8"} />
      </g>
      <Egg x={0} y={thread + 66} s={0.95} base={bases[v]} ink={inks[v]} accent={accents[v]} pattern={patterns[v]} shadow={false} />
    </svg>
  );
};
